import { Box, Grid, GridItem, Text, useColorModeValue } from '@chakra-ui/react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay } from 'date-fns';
import { CalendarActionItem } from '../useCalendarActionItems';
import { CalendarMilestone } from '../useCalendarMilestones';
import { isToday, isCurrentMonth } from '../calendar.util';

interface CalendarMonthViewProps {
  currentDate: Date;
  actionItems: CalendarActionItem[];
  milestones: CalendarMilestone[];
}

/**
 * Calendar Month View Component
 * Displays a monthly grid with action items and milestones
 */
export function CalendarMonthView({
  currentDate,
  actionItems,
  milestones,
}: CalendarMonthViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const cellBg = useColorModeValue('white', 'gray.800');
  const otherMonthBg = useColorModeValue('gray.50', 'gray.900');
  const todayBg = useColorModeValue('blue.50', 'blue.900');
  const todayTextColor = useColorModeValue('blue.600', 'blue.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const otherMonthTextColor = useColorModeValue('gray.400', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const actionItemBg = useColorModeValue('green.100', 'green.900');
  const actionItemBorder = useColorModeValue('green.600', 'green.400');
  const actionItemCompleteBg = useColorModeValue('gray.100', 'gray.700');
  const actionItemCompleteBorder = useColorModeValue('gray.400', 'gray.500');
  const milestoneBg = useColorModeValue('purple.100', 'purple.900');
  const milestoneBorder = useColorModeValue('purple.600', 'purple.400');
  const milestoneCompleteBg = useColorModeValue('gray.100', 'gray.700');
  const milestoneCompleteBorder = useColorModeValue('gray.400', 'gray.500');
  const moreTextColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const completeItemTextColor = useColorModeValue('gray.600', 'gray.400');

  const days = [];
  let day = startDate;

  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  const getItemsForDay = (day: Date) => {
    const dayActionItems = actionItems.filter((actionItem) => {
      if (!actionItem.dueDate) return false;
      const dueDate = actionItem.dueDate instanceof Date ? actionItem.dueDate : new Date(actionItem.dueDate);
      return isSameDay(dueDate, day);
    });

    const dayMilestones = milestones.filter((milestone) => {
      if (!milestone.dueDate) return false;
      const dueDate = milestone.dueDate instanceof Date ? milestone.dueDate : new Date(milestone.dueDate);
      return isSameDay(dueDate, day);
    });

    return { actionItems: dayActionItems, milestones: dayMilestones };
  };

  // Day headers
  const dayHeaders = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <Box>
      {/* Day Headers */}
      <Grid templateColumns="repeat(7, 1fr)" mb={2}>
        {dayHeaders.map((day) => (
          <Box key={day} textAlign="center" py={2} fontWeight="semibold" color={textColor}>
            {day.slice(0, 3)}
          </Box>
        ))}
      </Grid>

      {/* Calendar Grid */}
      <Grid templateColumns="repeat(7, 1fr)" gap={0} borderWidth={1} borderColor={borderColor} borderRadius="md" overflow="hidden">
        {days.map((day, index) => {
          const { actionItems: dayActionItems, milestones: dayMilestones } = getItemsForDay(day);
          const isCurrentMonthDay = isCurrentMonth(day, monthStart);
          const isTodayDay = isToday(day);
          const allItems = [...dayActionItems, ...dayMilestones];
          const displayItems = allItems.slice(0, 3);
          const moreCount = allItems.length - 3;

          return (
            <GridItem
              key={index}
              h="120px"
              borderWidth={1}
              borderColor={borderColor}
              p={2}
              bg={isCurrentMonthDay ? (isTodayDay ? todayBg : cellBg) : otherMonthBg}
              _hover={{ bg: hoverBg }}
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              <Text
                fontSize="sm"
                fontWeight="semibold"
                mb={2}
                color={isTodayDay ? todayTextColor : isCurrentMonthDay ? textColor : otherMonthTextColor}
                flexShrink={0}
              >
                {format(day, 'd')}
              </Text>

              {/* Render Items */}
              <Box flex={1} overflowY="auto" minH={0}>
                {displayItems.map((item, itemIndex) => {
                const isActionItem = 'description' in item;
                const isComplete = item.complete;
                const bg = isComplete
                  ? isActionItem
                    ? actionItemCompleteBg
                    : milestoneCompleteBg
                  : isActionItem
                  ? actionItemBg
                  : milestoneBg;
                const borderColor = isComplete
                  ? isActionItem
                    ? actionItemCompleteBorder
                    : milestoneCompleteBorder
                  : isActionItem
                  ? actionItemBorder
                  : milestoneBorder;
                const text = isActionItem ? item.description : item.task;

                return (
                  <Box
                    key={itemIndex}
                    fontSize="xs"
                    px={2}
                    py={1}
                    mb={1}
                    borderRadius="sm"
                    bg={bg}
                    borderLeftWidth={2}
                    borderLeftColor={borderColor}
                    cursor="pointer"
                    _hover={{ opacity: 0.8 }}
                    title={text}
                  >
                    <Text
                      fontWeight="medium"
                      isTruncated
                      textDecoration={isComplete ? 'line-through' : 'none'}
                      color={isComplete ? completeItemTextColor : textColor}
                    >
                      {isActionItem ? '📋' : '🎯'} {text}
                    </Text>
                  </Box>
                );
              })}
              </Box>

              {/* More indicator */}
              {moreCount > 0 && (
                <Text fontSize="xs" color={moreTextColor} mt={1} fontWeight="medium" flexShrink={0}>
                  +{moreCount} more
                </Text>
              )}
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
}

